import React, {
  forwardRef,
  PropsWithChildren,
  Ref,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";

import { ColDef, ColGroupDef, ColumnState, GridReadyEvent } from "@ag-grid-community/core";
import { ServerSideRowModelModule } from "@ag-grid-enterprise/server-side-row-model";

import IFieldModel from "@mapmycustomers/shared/types/fieldModel/IFieldModel";
import FilterModel from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import SortModel from "@mapmycustomers/shared/types/viewModel/internalModel/SortModel";

import ListViewDataFetcher from "@app/types/viewModel/ListViewDataFetcher";
import { convertToAgGridSortModel } from "@app/util/viewModel/convertSort";
import convertToAgGridFilterModel from "@app/util/viewModel/convertToAgGridFilterModel";

import BaseGrid, { BaseGridComponent, BaseGridProps } from "./BaseGrid";
import CheckboxSelectionHeader from "./gridComponents/headerCellRenderers/CheckboxSelectionHeader";
import TeamsHeader from "./gridComponents/headerCellRenderers/TeamsHeader";
import columnTypes from "./util/column-types/server-side-grid-column-types";
import PlatformDataSource from "./util/PlatformDataSource";

interface ServerSideGridMethods {
  refresh: () => void;
}

export interface ServerSideGridProps<T> extends BaseGridProps {
  columnToolPanelColumns?: (ColDef | ColGroupDef)[];
  fetcher: ListViewDataFetcher<T>;
  fieldModel: IFieldModel;
  initialFilterModel?: FilterModel;
  initialSortModel?: SortModel;
  onColumnModelChange?: (columnState: ColumnState[]) => void;
}

const ServerSideGrid = <T,>(
  {
    columnToolPanelColumns,
    fetcher,
    fieldModel,
    frameworkComponents,
    initialFilterModel,
    initialSortModel,
    modules,
    onColumnModelChange,
    onGridReady,
    ...gridProps
  }: PropsWithChildren<ServerSideGridProps<T>>,
  ref: Ref<ServerSideGridMethods>
) => {
  const modulesWithServerSideRowModel = useMemo(
    () => [ServerSideRowModelModule, ...(modules ?? [])],
    [modules]
  );

  const grid = useRef<BaseGridComponent>(null);
  const handleGridReady = useCallback(
    (e: GridReadyEvent) => {
      if (initialFilterModel) {
        e.api.setFilterModel(convertToAgGridFilterModel(initialFilterModel, fieldModel));
      }
      if (initialSortModel) {
        // FIXME: address aggrid's comment about using setSortModel:
        // as of version 24.0.0, setSortModel() is deprecated, sort information is now part of Column State. Please use columnApi.applyColumnState() instead
        e.api.setSortModel(convertToAgGridSortModel(initialSortModel));
      }
      e.api.setServerSideDatasource(new PlatformDataSource<T>(fetcher, fieldModel));
      onGridReady?.(e);
    },
    [fetcher, fieldModel, initialFilterModel, initialSortModel, onGridReady]
  );

  useImperativeHandle(ref, () => ({
    refresh: () => {
      // force grid to reload if external filter model has changed
      grid.current?.getGridApi()?.onFilterChanged();
    },
  }));

  return (
    <BaseGrid
      columnToolPanelColumns={columnToolPanelColumns}
      columnTypes={columnTypes}
      frameworkComponents={{
        checkboxSelectionHeader: CheckboxSelectionHeader,
        teamsHeader: TeamsHeader,
        ...frameworkComponents,
      }}
      modules={modulesWithServerSideRowModel}
      onColumnModelChange={onColumnModelChange}
      onGridReady={handleGridReady}
      ref={grid}
      rowModelType="serverSide"
      rowSelection="multiple"
      suppressMultiSort
      {...gridProps}
    />
  );
};

export interface ServerSideGridComponent<T>
  extends React.ComponentClass<ServerSideGridProps<T>>,
    ServerSideGridMethods {}

export default forwardRef(ServerSideGrid);
